/**
 * Name pool.
 *
 * The user supplies lines in "First - Last" form. Firsts and lasts are then
 * collected into independent pools and drawn from separately, so the
 * generator produces cross combinations rather than only the pairs as typed.
 *
 * A line starting with "#" opens a named group, and cross-combination happens
 * strictly inside a group. Without that, one flat list of Hindu and Muslim
 * names cross-combines into implausible hybrids - "Imran Sharma", "Aarav
 * Qureshi" - because a first from one tradition lands on a surname from the
 * other. A pool with no "#" lines behaves exactly as it always did: one
 * unnamed group holding everything.
 */

const STORAGE_KEY = "foliox-name-pool";

export const DEFAULT_POOL = [
  "# Hindu",
  "Aarav - Sharma",
  "Aditya - Verma",
  "Akash - Gupta",
  "Amit - Singh",
  "Anand - Kumar",
  "Aniket - Yadav",
  "Ankit - Joshi",
  "Anil - Patel",
  "Arjun - Shah",
  "Arun - Mehta",
  "Ashok - Desai",
  "Ashish - Trivedi",
  "Deepak - Chauhan",
  "Dinesh - Rathore",
  "Gaurav - Rajput",
  "Girish - Malhotra",
  "Harish - Kapoor",
  "Hemant - Chopra",
  "Jatin - Arora",
  "Kapil - Bhatia",
  "Karan - Saxena",
  "Kartik - Srivastava",
  "Kunal - Mishra",
  "Lalit - Tiwari",
  "Mahesh - Pandey",
  "Manish - Dubey",
  "Manoj - Shukla",
  "Mayank - Agarwal",
  "Mohit - Bansal",
  "Mukesh - Goyal",
  "Naveen - Jain",
  "Nikhil - Nair",
  "Nitin - Menon",
  "Pankaj - Pillai",
  "Parth - Iyer",
  "Prakash - Reddy",
  "Pranav - Rao",
  "Prashant - Naidu",
  "Praveen - Kulkarni",
  "Rahul - Deshmukh",
  "Rajesh - Deshpande",
  "Rakesh - Patil",
  "Ramesh - Jadhav",
  "Ravi - Shinde",
  "Rohan - Banerjee",
  "Rohit - Chatterjee",
  "Sachin - Mukherjee",
  "Sandeep - Ghosh",
  "Sanjay - Bose",
  "Satish - Das",
  "Saurabh - Dutta",
  "Shivam - Nayak",
  "Siddharth - Mohanty",
  "Sumit - Sahu",
  "Sunil - Bhat",
  "Suresh - Shetty",
  "Tarun - Hegde",
  "Tushar - Kamath",
  "Umesh - Pai",
  "Varun - Sethi",
  "Vijay - Khanna",
  "Vikas - Bajaj",
  "Vikram - Oberoi",
  "Vinay - Sinha",
  "Vinod - Prasad",
  "Vishal - Chandra",
  "Vivek - Krishnan",
  "Yash - Subramaniam",
  "Yogesh - Negi",
  "",
  "# Muslim",
  "Aamir - Khan",
  "Abdul - Ahmed",
  "Adnan - Ali",
  "Afzal - Ansari",
  "Ahmed - Siddiqui",
  "Akram - Qureshi",
  "Altaf - Sheikh",
  "Aman - Syed",
  "Anas - Hussain",
  "Anwar - Hashmi",
  "Arif - Rizvi",
  "Arshad - Naqvi",
  "Asif - Zaidi",
  "Aslam - Abbasi",
  "Ayaan - Farooqui",
  "Azhar - Usmani",
  "Bilal - Mirza",
  "Danish - Baig",
  "Faisal - Pathan",
  "Faizan - Chishti",
  "Farhan - Alvi",
  "Farooq - Raza",
  "Firoz - Malik",
  "Hamid - Mansoori",
  "Hamza - Idrisi",
  "Haris - Saifi",
  "Haroon - Salmani",
  "Hasan - Warsi",
  "Ibrahim - Nadvi",
  "Idris - Shaikh",
  "Imran - Memon",
  "Irfan - Sait",
  "Javed - Kazi",
  "Junaid - Patel",
  "Kamran - Rangrez",
  "Kashif - Dalvi",
  "Khalid - Shaikh",
  "Mansoor - Attar",
  "Masood - Bagwan",
  "Mohsin - Tamboli",
  "Mubashir - Momin",
  "Mudassir - Inamdar",
  "Nadeem - Mulla",
  "Nasir - Jagirdar",
  "Naved - Sayyed",
  "Noman - Shah",
  "Owais - Sheikh",
  "Parvez - Deshmukh",
  "Qasim - Ansari",
  "Rafiq - Khan",
  "Rashid - Siddiqui",
  "Rehan - Qureshi",
  "Rizwan - Mirza",
  "Sadiq - Hussain",
  "Saeed - Ahmed",
  "Sajid - Ali",
  "Salman - Syed",
  "Sameer - Pathan",
  "Shahid - Malik",
  "Shakeel - Baig",
  "Sohail - Raza",
  "Tahir - Abbasi",
  "Tanveer - Zaidi",
  "Tariq - Naqvi",
  "Umar - Rizvi",
  "Usman - Hashmi",
  "Wasim - Farooqui",
  "Yasin - Usmani",
  "Yusuf - Chishti",
  "Zaheer - Alvi",
  "Zahid - Shaikh",
  "Zeeshan - Ansari",
  "Zubair - Khan",
].join("\n");

/**
 * Splits raw text into groups, each holding its own independent first/last
 * pools. Junk lines are ignored. Lines before any "#" header land in an
 * unnamed leading group, so a pool that never uses headers parses to exactly
 * one group.
 */
export function parsePool(text) {
  const groups = [];
  let current = null;

  const openGroup = (name) => {
    current = { name, firsts: [], lasts: [] };
    groups.push(current);
  };

  String(text || "")
    .split(/\r?\n/)
    .forEach((line) => {
      const trimmed = line.trim();
      if (!trimmed) return;

      if (trimmed.startsWith("#")) {
        openGroup(trimmed.slice(1).trim());
        return;
      }

      // Accept "-", en dash and em dash as the separator.
      const parts = trimmed.split(/\s*[-–—]\s*/);
      const first = (parts[0] || "").trim();
      const last = (parts.slice(1).join(" ") || "").trim();
      if (!first && !last) return;

      if (!current) openGroup("");
      if (first) current.firsts.push(first);
      if (last) current.lasts.push(last);
    });

  return groups.filter((g) => g.firsts.length || g.lasts.length);
}

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

/**
 * Draws a first and a last independently from within one group. The group is
 * chosen in proportion to how many names it holds, so a pool of 70 Hindu and
 * 73 Muslim names draws from each about equally, and adding ten names to one
 * group shifts the odds by ten names' worth rather than flipping a coin
 * between two lists of wildly different sizes.
 *
 * Returns null when the pool has nothing usable, so callers can surface that
 * rather than render "undefined".
 */
export function randomNameFrom(text) {
  const groups = parsePool(text).filter((g) => g.firsts.length);
  if (!groups.length) return null;

  const total = groups.reduce((n, g) => n + g.firsts.length, 0);
  let roll = Math.random() * total;
  let group = groups[groups.length - 1];
  for (const candidate of groups) {
    roll -= candidate.firsts.length;
    if (roll < 0) {
      group = candidate;
      break;
    }
  }

  const first = pick(group.firsts);
  const last = group.lasts.length ? pick(group.lasts) : "";
  return last ? `${first} ${last}` : first;
}

export function poolStats(text) {
  const groups = parsePool(text);
  return groups.reduce(
    (acc, g) => ({
      firsts: acc.firsts + g.firsts.length,
      lasts: acc.lasts + g.lasts.length,
      // Only same-group pairings are ever produced, so the reachable total is
      // the sum per group, not the product of the two combined pools.
      combinations: acc.combinations + g.firsts.length * (g.lasts.length || 1),
    }),
    { firsts: 0, lasts: 0, combinations: 0 }
  );
}

const VERSION_KEY = "foliox-name-pool-version";
const POOL_VERSION = "2";

/* Lines that only ever appeared in the version 1 shipped default. All three
   present means the stored pool is that default sitting untouched in a
   browser, rather than a list someone actually wrote. */
const V1_DEFAULT_MARKERS = ["Zara - Ali", "Ananya - Patel", "Yuki - Deshmukh"];

/**
 * Anyone who used the app before version 2 has the old default sitting in
 * localStorage, which would otherwise shadow the new one forever. That copy
 * is replaced once; a pool the user edited is left alone, since it is their
 * text and not ours to discard.
 */
export function loadPool() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return DEFAULT_POOL;
    if (localStorage.getItem(VERSION_KEY) === POOL_VERSION) return saved;

    localStorage.setItem(VERSION_KEY, POOL_VERSION);
    if (V1_DEFAULT_MARKERS.every((marker) => saved.includes(marker))) {
      localStorage.setItem(STORAGE_KEY, DEFAULT_POOL);
      return DEFAULT_POOL;
    }
    return saved;
  } catch (err) {
    return DEFAULT_POOL;
  }
}

export function savePool(text) {
  try {
    localStorage.setItem(STORAGE_KEY, text);
    localStorage.setItem(VERSION_KEY, POOL_VERSION);
  } catch (err) {
    /* storage may be unavailable; the pool still works for this session */
  }
}
